import { Drawer } from "@mui/material";
import { Currency } from "@redotech/money/currencies";
import { useHandler } from "@redotech/react-util/hook";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { CreateFulfillmentOrder } from "@redotech/redo-model/fulfillments/create-fulfillment-order";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { RedoFulfillmentLocation } from "@redotech/redo-model/fulfillments/fulfillment-location";
import { FulfillmentOrderLineItem } from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import { IMoney } from "@redotech/redo-model/outbound-labels/util";
import { omsAddressAutocompleteForm } from "@redotech/redo-web/address-autocomplete-form";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { DeprecatedDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-date-picker";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { IconButton } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { memo, useMemo, useState } from "react";
import { AddressForm } from "./address-form";
import { ItemTable } from "./item-table";
import * as styles from "./order-form-slideout.module.css";

const orderInfoForm = groupInput({
  orderNumber: input<string>({}),
  orderDate: input<Date>({}),
  paidDate: input<Date>({}),
  shippingPaid: input<IMoney>({}),
  taxPaid: input<IMoney>({}),
  totalPaid: input<IMoney>({}),
  items: input<FulfillmentOrderLineItem[]>({}),
  internalNote: input<string>({}),
});

export const manualOrderForm = groupInput({
  location: input<RedoFulfillmentLocation | null>({}),
  address: omsAddressAutocompleteForm,
  orderInfo: orderInfoForm,
});
export type ManualOrderForm = InputSchema.Input<typeof manualOrderForm>;
export type ManualOrderFormValue = InputSchema.Value<typeof manualOrderForm>;

export interface ManualOrderSlideoutProps {
  form: ManualOrderForm;
  rpcCall: (
    fulfillmentOrder: CreateFulfillmentOrder,
  ) => Promise<{ success: boolean; fulfillmentGroups: FulfillmentGroupData[] }>;
  onClose: () => void;
  open: boolean;
  title: string;
  submitButtonText: string;
  onSuccess: (groups: FulfillmentGroupData[]) => void;
  successMessage: string;
  errorMessage: string;
  locations: readonly RedoFulfillmentLocation[];
  itemTableResetKey?: number;
}

export const ManualOrderSlideout = memo(function ManualOrderSlideout({
  form,
  rpcCall,
  onSuccess,
  onClose,
  open,
  title,
  submitButtonText,
  successMessage,
  errorMessage,
  locations,
  itemTableResetKey,
}: ManualOrderSlideoutProps) {
  const [loading, setLoading] = useState<boolean>(false);

  const isSubmitDisabled = useMemo(() => {
    return form.allErrors.length > 0;
  }, [form]);

  const handleSubmit = useHandler(async () => {
    try {
      setLoading(true);
      // Update the total prices based off of the changes to the quantity
      // Unit discount price and total discount price are the same as the unit price and total price
      const data: CreateFulfillmentOrder = {
        ...form.value,
        orderInfo: {
          ...form.value.orderInfo,
          items: form.value.orderInfo.items.map((item) => ({
            ...item,
            unitDiscountedPrice: {
              amount: item.unitPrice.amount,
              currency: item.unitPrice.currency,
            },
            totalPrice: {
              amount: (
                Number(item.unitPrice.amount) * item.totalQuantity
              ).toString(),
              currency: item.unitPrice.currency,
            },
            totalDiscountedPrice: {
              amount: (
                Number(item.unitPrice.amount) * item.totalQuantity
              ).toString(),
              currency: item.unitPrice.currency,
            },
            unfulfilledQuantity: item.totalQuantity,
            internalNotes: form.value.orderInfo.internalNote,
          })),
        },
      };

      const { success, fulfillmentGroups } = await rpcCall(data);

      if (success) {
        toast(successMessage, { variant: "success" });
        onClose();
        onSuccess(fulfillmentGroups);
      } else {
        toast(errorMessage, { variant: "error" });
      }
    } catch (error) {
      toast(errorMessage, { variant: "error" });
      console.error(error);
    } finally {
      setLoading(false);
    }
  });

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={open}
      sx={{ "& .MuiDrawer-paper": { overflow: "hidden" } }}
    >
      <Flex className={styles.drawerContainer} dir="column">
        <Flex className={styles.header} dir="column" gap="none">
          <Flex
            align="flex-start"
            borderBottomWidth="1px"
            borderColor="primary"
            borderStyle="solid"
            dir="row"
            justify="space-between"
            p="3xl"
          >
            <Flex dir="column" gap="xs">
              <Text fontSize="xl" fontWeight="semibold">
                {title}
              </Text>
            </Flex>
            <IconButton onClick={onClose}>
              <XIcon />
            </IconButton>
          </Flex>
        </Flex>
        <Flex className={styles.content} dir="column" gap="none" w="full">
          <Flex dir="row" gap="none" justify="space-between" w="full">
            <Flex
              className={styles.orderFormColumn}
              dir="column"
              flex="1"
              gap="3xl"
              p="3xl"
            >
              <AddressForm
                form={form.inputs.address}
                requirePhone={false}
                title="Recipient information"
              />
            </Flex>
            <Flex
              bgColor="tertiary"
              className={styles.orderFormColumn}
              dir="column"
              flex="1"
              gap="3xl"
              p="3xl"
            >
              <Text fontSize="md" fontWeight="semibold">
                Order details
              </Text>
              <RedoTextInput
                label="Order #"
                placeholder="Order number will be generated if not provided"
                setValue={form.inputs.orderInfo.inputs.orderNumber.setValue}
                value={form.value.orderInfo.orderNumber}
              />
              <Flex dir="column" gap="xs">
                <RedoSingleSelectDropdownInput<RedoFulfillmentLocation>
                  label="Location"
                  options={locations.map((location) => ({
                    id: location.externalId,
                    type: "text",
                    text: location.name,
                    value: location,
                  }))}
                  optionSelected={({ item: location }) => {
                    form.inputs.location.setValue(location.value);
                  }}
                  selectedItem={
                    form.value.location
                      ? {
                          id: form.value.location.externalId,
                          type: "text",
                          text: form.value.location.name,
                          value: form.value.location,
                        }
                      : undefined
                  }
                  size="xs"
                />
              </Flex>
              <Flex dir="column" gap="xs">
                <Text fontSize="xs" textColor="tertiary">
                  Order date
                </Text>
                <DeprecatedDatePicker
                  buttonSize="sm"
                  date={form.value.orderInfo.orderDate}
                  maxDate={new Date()}
                  setDate={(date) => {
                    form.inputs.orderInfo.inputs.orderDate.setValue(date);
                  }}
                />
              </Flex>
              <Flex dir="column" gap="xs">
                <Text fontSize="xs" textColor="tertiary">
                  Paid date
                </Text>
                <DeprecatedDatePicker
                  buttonSize="sm"
                  date={form.value.orderInfo.paidDate}
                  maxDate={new Date()}
                  setDate={(date) => {
                    form.inputs.orderInfo.inputs.paidDate.setValue(date);
                  }}
                />
              </Flex>
              <RedoIncrementDecrement
                fullWidth
                label="Shipping paid (USD)"
                min={0}
                setValue={(value) => {
                  form.inputs.orderInfo.inputs.shippingPaid.setValue({
                    amount: value.toString(),
                    currency: Currency.USD,
                  });
                }}
                size="small"
                value={Number(form.value.orderInfo.shippingPaid.amount)}
              />
              <RedoIncrementDecrement
                fullWidth
                label="Tax paid (USD)"
                min={0}
                setValue={(value) => {
                  form.inputs.orderInfo.inputs.taxPaid.setValue({
                    amount: value.toString(),
                    currency: Currency.USD,
                  });
                }}
                size="small"
                value={Number(form.value.orderInfo.taxPaid.amount)}
              />
              <RedoIncrementDecrement
                fullWidth
                label="Total paid (USD)"
                min={0}
                setValue={(value) => {
                  form.inputs.orderInfo.inputs.totalPaid.setValue({
                    amount: value.toString(),
                    currency: Currency.USD,
                  });
                }}
                size="small"
                value={Number(form.value.orderInfo.totalPaid.amount)}
              />
              <RedoTextInput
                label="Internal notes"
                placeholder="Enter internal notes"
                setValue={form.inputs.orderInfo.inputs.internalNote.setValue}
                value={form.value.orderInfo.internalNote}
              />
            </Flex>
          </Flex>
          <ItemTable
            initialItems={form.value.orderInfo.items}
            key={itemTableResetKey}
            onItemsChange={(items) => {
              form.inputs.orderInfo.inputs.items.setValue(items);
            }}
          />
        </Flex>
        <Flex
          align="center"
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          borderTopWidth="1px"
          className={styles.footer}
          dir="row"
          justify="space-between"
          p="3xl"
          w="full"
        >
          <RedoButton
            className={styles.cancelButton}
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Cancel"
            theme="normal"
          />
          <RedoButton
            className={styles.createButton}
            disabled={isSubmitDisabled}
            hierarchy="primary"
            onClick={handleSubmit}
            pending={loading}
            size="md"
            text={submitButtonText}
            theme="normal"
            type="submit"
          />
        </Flex>
      </Flex>
    </Drawer>
  );
});
